import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { Check } from "lucide-react";
import type { KarlWizardStep } from "../karlWizard";

interface KarlWizardCardProps {
  step: KarlWizardStep;
  position: number;
  total: number;
  isLive: boolean;
  isDone: boolean;
  /**
   * True once he has moved past this step. A step still ahead of him is
   * neither done nor skipped — labelling it "Skipped" told him he had passed
   * on work he had not been offered yet.
   */
  isPast: boolean;
  /** The step's work is underway — lock the buttons rather than hide them. */
  inFlight: boolean;
  /** An earlier step has to be settled first: say so instead of offering Yes. */
  isLocked?: boolean;
  onYes: () => void;
  /** Absent when the step after this one is locked: skipping would land on a wall. */
  onSkip?: () => void;
  /** Absent: the walkthrough cannot be ended halfway. */
  onStop?: () => void;
}

/**
 * One card, one question. A card the estimator has answered stays in the thread
 * with its question still readable and a badge where its buttons were, so
 * scrolling up reads question, answer, question, answer — rather than a stale
 * call to action they already dealt with.
 */
export const KarlWizardCard = ({
  step,
  position,
  total,
  isLive,
  isDone,
  isPast,
  inFlight,
  isLocked = false,
  onYes,
  onSkip,
  onStop,
}: KarlWizardCardProps) => {
  const { t } = useTranslation();

  const eyebrow = !isLive
    ? t("smartSurveyor.cablesBeta.wizardStepPast", { step: position })
    : total > 1
      ? t("smartSurveyor.cablesBeta.wizardStepLive", { step: position, total })
      : t("smartSurveyor.cablesBeta.wizardStepOnly");

  return (
    <div
      data-karl-step={step.key}
      data-karl-live={isLive ? "1" : "0"}
      className={clsx(
        "karl-rise rounded-xl border px-3 py-3 text-[13px] transition-colors",
        isLive
          ? "border-blue-200 bg-blue-50/60"
          : "border-slate-200 bg-slate-50/60",
      )}
    >
      <div className="mb-2 flex items-center gap-1.5">
        <span
          className={clsx(
            "inline-block h-1.5 w-1.5 rounded-full",
            isLive ? "bg-blue-600" : "bg-slate-300",
          )}
        />
        <span
          className={clsx(
            "text-[10px] font-semibold uppercase tracking-[0.1em]",
            isLive ? "text-blue-700" : "text-slate-400",
          )}
        >
          {eyebrow}
        </span>
        {!isLive && (isDone || isPast) && (
          <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold">
            {isDone ? (
              <>
                <Check size={10} className="text-emerald-600" />
                <span className="text-emerald-700">
                  {t("smartSurveyor.cablesBeta.wizardDone")}
                </span>
              </>
            ) : (
              <span className="text-slate-400">
                {t("smartSurveyor.cablesBeta.wizardSkipped")}
              </span>
            )}
          </span>
        )}
      </div>

      <div
        className={clsx(
          "leading-snug",
          isLive ? "mb-3 text-slate-800" : "text-slate-500",
        )}
      >
        {step.question}
      </div>

      {isLive && isLocked && (
        <div className="text-[13px] text-slate-600">
          {t(`smartSurveyor.cablesBeta.${step.lockedKey ?? "wizardBoardsFirst"}`)}
        </div>
      )}

      {isLive && !isLocked && (
        // The bottom thinking row is the only place that says Karl is working;
        // dimming here says it about this step without a second banner.
        <div
          className={clsx(
            "flex flex-col gap-1.5",
            inFlight && "pointer-events-none select-none opacity-50",
          )}
        >
          <button
            type="button"
            disabled={inFlight}
            onClick={onYes}
            className="w-full rounded-md bg-blue-600 px-3 py-2 text-left text-[13px] font-semibold text-white transition-opacity hover:opacity-90"
          >
            {t("smartSurveyor.cablesBeta.wizardYes")}
          </button>
          {onSkip && (
            <button
              type="button"
              disabled={inFlight}
              onClick={onSkip}
              className="w-full rounded-md bg-slate-100 px-3 py-2 text-left text-[12.5px] text-slate-700 transition-colors hover:bg-slate-200"
            >
              {t("smartSurveyor.cablesBeta.wizardSkip")}
            </button>
          )}
          {onStop && (
            <button
              type="button"
              disabled={inFlight}
              onClick={onStop}
              className="w-full rounded-md bg-slate-50 px-3 py-2 text-left text-[12.5px] text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-700"
            >
              {t("smartSurveyor.cablesBeta.wizardStop")}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
